import React, { useState } from "react";
import ShelfList from "./ShelfList";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useDeleteItem } from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";

const RackTabView = ({
  racks,
  setRacks,
  isEditMode,
  generateName,
  pharmacy_id,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // Track which rack is being confirmed for deletion

  const dlt_mutation = useDeleteItem(ApiEndPoint.PHARMACY);

  const updateCategory = (value) => {
    const updatedRacks = [...racks];
    updatedRacks[selectedTab].category = value;
    setRacks(updatedRacks);
  };

  const addRack = () => {
    const newRackIndex = racks.length;
    const newRack = {
      rack_name: generateName(newRackIndex, "Rack"),
      category: `Category ${newRackIndex + 1}`,
      pharmacy_id: pharmacy_id,
      shelves: [],
    };
    setRacks([...racks, newRack]);
    setSelectedTab(newRackIndex); // Automatically select the newly added rack
  };

  const handleDeleteClick = (rackIndex, id) => {
    setConfirmDelete({ rackIndex: rackIndex, id: id }); // Show confirmation for the selected rack
  };

  const confirmDeleteRack = (rackIndex) => {
    const updatedRacks = racks.filter(
      (_, index) => index !== rackIndex.rackIndex
    );
    setRacks(updatedRacks);

    if (rackIndex.id) {
      dlt_mutation.mutate(
        { data: { action: "delRacks", id: rackIndex.id } },
        {
          onSuccess: () => {
            toast.success("Rack Deleted!");
          },
        }
      );
    }
    // Adjust selected tab index
    if (rackIndex === selectedTab && updatedRacks.length > 0) {
      setSelectedTab(Math.max(0, rackIndex - 1));
    } else if (updatedRacks.length === 0) {
      setSelectedTab(0); // Reset to first tab if no racks are left
    }
    setConfirmDelete(null); // Reset confirmation state
  };

  const cancelDelete = () => {
    setConfirmDelete(null); // Cancel deletion
  };

  return (
    <div className="flex">
      {/* Sidebar for Rack Tabs */}
      <div className="w-1/4 bg-gray-100 p-4 rounded-l shadow">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Racks</h3>
        <div className="space-y-2">
          {racks.map((rack, index) => (
            <div
              key={index}
              className={`flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer ${
                selectedTab === index
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-800 border border-gray-300"
              } transition-all hover:shadow-md`}
              onClick={() => setSelectedTab(index)}
            >
              <span>{rack.rack_name}</span>
              {isEditMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the tab selection
                    handleDeleteClick(index, rack.id);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addRack}
              className="w-full mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-lg flex items-center justify-center"
            >
              <FaPlus className="mr-2" /> Add Rack
            </button>
          )}
        </div>
      </div>

      {/* Main Content for Rack Details */}
      <div className="w-3/4 bg-gray-50 p-4 rounded-r shadow">
        {racks.length > 0 ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Rack Category
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={racks[selectedTab]?.category || ""}
                  onChange={(e) => updateCategory(e.target.value)}
                  className="mt-1 border border-gray-300 rounded px-4 py-2 w-full"
                />
              ) : (
                <p className="mt-1 text-gray-800">
                  {racks[selectedTab]?.category || "No Category"}
                </p>
              )}
            </div>
            <ShelfList
              shelves={racks[selectedTab]?.shelves || []}
              rackIndex={selectedTab}
              racks={racks}
              setRacks={setRacks}
              isEditMode={isEditMode}
              generateName={generateName}
            />
          </>
        ) : (
          <p className="text-center text-gray-600">No racks available.</p>
        )}
      </div>

      {/* Centered Confirmation Modal */}
      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg text-center">
            <p className="text-lg font-medium text-gray-800">
              Are you sure you want to delete this rack?
            </p>
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => confirmDeleteRack(confirmDelete)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Yes
              </button>
              <button
                onClick={cancelDelete}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RackTabView;
